import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  IconButton,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useQuery } from '@apollo/client/react';
import { GetUnpaidInvoicesDocument, GetUnpaidInvoicesQuery, Invoice } from '../../../generated/graphql';
import { useAlert } from '@dwelvio/react-alert';
import { useApp } from '../../../components/app-context';

interface InvoiceSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (invoice: Invoice) => void;
}

export function InvoiceSelectionDialog({ open, onClose, onSelect }: InvoiceSelectionDialogProps) {
  const { show } = useAlert();
  const app = useApp();
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  const { data, loading, error } = useQuery<GetUnpaidInvoicesQuery>(GetUnpaidInvoicesDocument, {
    skip: !open,
    fetchPolicy: 'network-only',
  });

  React.useEffect(() => {
    if (error) {
      show.error('Nepodarilo sa načítať faktúry.', error.message);
    }
  }, [error, show]);

  const invoices = data?.getUnpaidInvoices ?? [];

  const columns = React.useMemo((): GridColDef[] => {
    return [
      {
        field: 'invoiceNo',
        headerName: 'Faktúra',
        width: 80,
      },
      {
        field: 'contract',
        headerName: 'Zmluva',
        width: 250,
        renderCell: (params: GridRenderCellParams<Invoice>) => {
          const contract = params.row.contract;
          return contract ? <span>{contract.title}</span> : <span>-</span>;
        },
      },

      {
        field: 'refNo',
        headerName: 'VS',
        width: 80,
      },

      {
        field: 'totalAmount',
        headerName: 'Suma',
        width: 80,
        valueFormatter: (value: number) => app.formatNumber(value, 2) + ' €',
      },
      {
        field: 'paidAmount',
        headerName: 'Zaplatené',
        width: 80,
        valueFormatter: (value: number) => app.formatNumber(value, 2) + ' €',
      },
      {
        field: 'remaining',
        headerName: 'Zostáva',
        width: 80,
        renderCell: (params: GridRenderCellParams<Invoice>) => {
          const remaining = params.row.totalAmount - (params.row.paidAmount || 0);
          return <span>{app.formatNumber(remaining, 2)} €</span>;
        },
      },
      {
        field: 'description',
        headerName: 'Popis',
        flex: 1,
        minWidth: 250,
      },
    ];
  }, [app]);

  const handleSelect = () => {
    if (selectedInvoice) {
      onSelect(selectedInvoice);
      setSelectedInvoice(null);
    }
  };

  const handleClose = () => {
    setSelectedInvoice(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          Výber faktúry
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ height: 500, width: '100%' }}>
          <DataGrid
            rows={invoices}
            columns={columns}
            loading={loading}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10,
                },
              },
              sorting: {
                sortModel: [{ field: 'invoiceNo', sort: 'desc' }],
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick={false}
            onRowClick={(params) => setSelectedInvoice(params.row)}
            sx={{
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
              },
              '& .MuiDataGrid-row.Mui-selected': {
                backgroundColor: 'action.selected',
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Zrušiť</Button>
        <Button onClick={handleSelect} variant="contained" disabled={!selectedInvoice}>
          Vybrať
        </Button>
      </DialogActions>
    </Dialog>
  );
}
